<template>
    <div ref="videoAreaRef" id="videoArea" class="wrapper" :style="{ height: videoAreaHeight }">
        <video ref="videoRef" autoplay muted playsinline class="video" />
        <div class="live-indicator" :class="isLive ? 'live' : 'stale'"></div>
        <div class="motion">
          <span class="box-white left">{{ controlPLC.motion.leftPWM }}</span>
          <span class="box-white right">{{ controlPLC.motion.rightPWM }}</span>
        </div>
    </div>
</template>

<script>
import mpegts from 'mpegts.js'

export default {
  name: 'videoMonitor',
  props: {
    controlPLC: { type: Object, required: true }
  },
  data() {
    return {
      heightData: 500,
      videoWidth: 500,
      lastWidth: 0,
      player: null,
      isLive: true,
      lastUpdate: Date.now(),
      checkInterval: null,
      observer: null,
      destroying: false,
      mountedReady: false
    }
  },
  computed: {
    videoAreaHeight() {
      return `${this.videoWidth * (1068 / 600)}px`
    },
    host() {
      return this.$store.state.host
    }
  },
  methods: {
    buildStreamUrl() {
      const base = (this.$store.state.axiosAddr || '/cfr').replace(/\/$/, '')
      const machine = localStorage.selected_machine || ''
      const token = localStorage.token || ''
      return `${base}/test?selected_machine=${machine}&value[path]=/video&value[port]=8080&token=${token}`
    },
    onPlayerError(errorType, errorDetail, errorInfo) {
      if (this.destroying) return
      console.warn('[videoMonitor] stream error', errorType, errorDetail, errorInfo)
      this.isLive = false
    },
    bindPlayerEvents(player) {
      if (!player) return
      if (mpegts.Events?.ERROR) player.on(mpegts.Events.ERROR, this.onPlayerError)
      player.on('error', this.onPlayerError)
    },
    unbindPlayerEvents(player) {
      if (!player) return
      try {
        if (mpegts.Events?.ERROR) player.off(mpegts.Events.ERROR, this.onPlayerError)
        player.off('error', this.onPlayerError)
      } catch (e) {
        /* player may already be torn down */
      }
    },
    startLiveCheck() {
      if (this.checkInterval) clearInterval(this.checkInterval)
      this.checkInterval = setInterval(() => {
        this.isLive = (Date.now() - this.lastUpdate) < 1000
      }, 500)
    },
    destroyPlayer() {
      this.destroying = true
      if (this.player) {
        const player = this.player
        this.player = null
        try {
          player.pause()
        } catch (e) { /* ignore */ }
        try {
          player.unload()
        } catch (e) { /* ignore */ }
        try {
          player.detachMediaElement()
        } catch (e) { /* ignore */ }
        try {
          player.destroy()
        } catch (e) {
          console.warn('[videoMonitor] destroy', e)
        }
        this.unbindPlayerEvents(player)
      }
      const videoElement = this.$refs.videoRef
      if (videoElement) {
        videoElement.ontimeupdate = null
        videoElement.onerror = null
      }
      this.destroying = false
    },
    startPlayer() {
      if (!this.mountedReady) return

      const videoElement = this.$refs.videoRef
      if (!mpegts.getFeatureList().mseLivePlayback || !videoElement) {
        return
      }
      if (!localStorage.token || !localStorage.selected_machine) {
        return
      }

      this.destroyPlayer()

      try {
        const url = this.buildStreamUrl()
        const player = mpegts.createPlayer({
          type: 'mpegts',
          isLive: true,
          url,
          liveSync: true,
          enableStashBuffer: false
        })

        this.player = player
        this.bindPlayerEvents(player)

        player.attachMediaElement(videoElement)
        player.load()

        const playPromise = player.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch((err) => {
            if (!this.destroying) {
              console.warn('[videoMonitor] play failed', err)
              this.isLive = false
            }
          })
        }

        videoElement.ontimeupdate = () => {
          this.lastUpdate = Date.now()
        }
        videoElement.onerror = () => {
          if (!this.destroying) this.isLive = false
        }

        this.startLiveCheck()
      } catch (e) {
        console.warn('[videoMonitor] startPlayer', e)
        this.isLive = false
        this.destroyPlayer()
      }
    },
    startResizeObserver() {
      const videoAreaElement = this.$refs.videoAreaRef

      if (videoAreaElement) {
        this.observer = new ResizeObserver(entries => {
          for (const entry of entries) {
            const newWidth = entry.contentRect.width
            if (Math.abs(this.lastWidth - newWidth) > 1) {
              this.lastWidth = newWidth
              window.requestAnimationFrame(() => {
                this.videoWidth = newWidth
                this.heightData = newWidth
              })
            }
          }
        })
        this.observer.observe(videoAreaElement)
      }
    }
  },
  mounted() {
    this.mountedReady = true
    this.$nextTick(() => {
      this.startPlayer()
      this.startResizeObserver()
    })
  },
  beforeUnmount() {
    this.mountedReady = false
    this.destroyPlayer()
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
    }
  }
}
</script>


<style scoped>
.motion {
  position: absolute;
  bottom: -1.5em;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.motion span {
  padding: 5px 10px;
  font-size: 1.5em;
  width: 50px;
  text-align: center;
}

.wrapper {
  width: 100%;
  min-height: 250px;
  max-width: 400px !important;
  border: 1px solid red;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.video {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg) scale(1.78);
  transform-origin: center center;
  object-fit: contain;
}

.live-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 1.5em;
  height: 1.5em;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.live-indicator.live {
  background-color: #00ff00; /* yeşil */
}

.live-indicator.stale {
  background-color: #ff0000; /* kırmızı */
}
</style>
